import { onMounted, watch } from "vue";
import { storeToRefs } from "pinia";
import { useDebounceFn } from "@vueuse/core";
import { useCampaignsPivotStore, type MetricsView, type PivotMode } from "@stores/useCampaignsPivotStore";
import { useSurveysStore } from "@stores/surveys";
import { arrayParam, stringParam, useFiltersUrlSync, type UrlParamCodec } from "@composables/filters/useFiltersUrlSync";
import { PIVOT_DIMENSIONS, checkedValues, picksFromChecked } from "@views/Admin/CampaignsPivot/pivotDimensions";
import type { PivotRespondentGroup } from "@/types";

// Respondent groups are nested objects (conditions, answers…): the one param
// that stays JSON. Everything else reads as plain query params.
const jsonParam = <T>(): UrlParamCodec<T | null> => ({
  encode: (value) => (value == null || (Array.isArray(value) && !value.length) ? null : JSON.stringify(value)),
  decode: (raw) => {
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (typeof value !== "string" || !value) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  },
});

const filterParams = Object.fromEntries(PIVOT_DIMENSIONS.map((d) => [`f.${d.filterKey}`, arrayParam()]));

const urlParams = {
  surveys: arrayParam(Number),
  accounts: arrayParam(Number),
  from: stringParam(),
  to: stringParam(),
  mode: stringParam(),
  view: stringParam(),
  rows: arrayParam(),
  cols: arrayParam(),
  metrics: arrayParam(),
  groups: jsonParam<PivotRespondentGroup[]>(),
  ...filterParams,
};

export function usePivotUrlSync() {
  const store = useCampaignsPivotStore();
  const surveysStore = useSurveysStore();

  const {
    selectedAccountIds,
    selectedSurveyIds,
    rowDimensions,
    columnDimensions,
    rowFilters,
    dateFrom,
    dateTo,
    pivotMode,
    metricsView,
    selectedMetrics,
    respondentGroups,
  } = storeToRefs(store);

  const { syncUrl, restoreFromUrl } = useFiltersUrlSync({
    params: urlParams,
    serialize: () => ({
      surveys: selectedSurveyIds.value,
      accounts: selectedAccountIds.value,
      from: dateFrom.value ?? "",
      to: dateTo.value ?? "",
      mode: pivotMode.value === "metrics" ? "" : pivotMode.value,
      view: metricsView.value === "combined" ? "" : metricsView.value,
      rows: checkedValues(rowDimensions.value),
      cols: checkedValues(columnDimensions.value),
      metrics: selectedMetrics.value,
      groups: respondentGroups.value,
      ...Object.fromEntries(PIVOT_DIMENSIONS.map((d) => [`f.${d.filterKey}`, rowFilters.value[d.filterKey] ?? []])),
    }),
    apply: async (state) => {
      // Hydrate lightSurveys so the Select can label the selection.
      await Promise.all(state.surveys.map((id) => surveysStore.getSurveyLight(id)));
      await store.applyState({
        accountIds: state.accounts,
        surveyIds: state.surveys,
        rowDimensions: picksFromChecked(state.rows),
        columnDimensions: picksFromChecked(state.cols),
        rowFilters: Object.fromEntries(
          PIVOT_DIMENSIONS.map((d) => [d.filterKey, (state as Record<string, unknown>)[`f.${d.filterKey}`] as string[]]).filter(
            ([, values]) => values.length,
          ),
        ),
        dateFrom: state.from || null,
        dateTo: state.to || null,
        pivotMode: (state.mode || "metrics") as PivotMode,
        metricsView: (state.view || "combined") as MetricsView,
        selectedMetrics: state.metrics.length ? state.metrics : null,
        respondentGroups: state.groups ?? [],
      });
    },
  });

  onMounted(() => {
    if (!restoreFromUrl()) syncUrl();
  });

  watch(
    [
      selectedAccountIds, selectedSurveyIds, dateFrom, dateTo, pivotMode, metricsView, selectedMetrics,
      rowFilters, respondentGroups, rowDimensions, columnDimensions,
    ],
    useDebounceFn(syncUrl, 300),
    { deep: true },
  );
}
